import { useEffect, useRef } from 'react';
import type { JSX, ReactNode } from 'react';
import {
  Avatar,
  Card,
  IconBusinessIdeaMoney,
  IconCalendarMark,
  IconPieChart,
  IconSignHashtag,
  IconTextBar,
  IconUserSingleNeutralMale,
  StatusBadge,
  datePure,
  euros,
  initialsOf,
} from '@taki/ui';
import type { Cell } from '@taki/ui';
import { useMoteurVanilla } from '@taki/app-runtime';
import type { ProprietesDeVueCanvas } from '@taki/app-runtime';
import { monterAnneau } from './anneau-d-avancement.js';
import type { AnneauDAvancement } from './anneau-d-avancement.js';

/**
 * « FICHE RÉSUMÉE » : une VUE DE FICHE Canvas des projets (regle G23, accord
 * dans `canvas.views` de `taki.app.json`, carte dans `vuesCustom` de
 * `taki.screens.json`, chantier `fiche-suivi`).
 *
 * LES MEMES INFORMATIONS QUE LA FICHE, AUTREMENT (atelier du 26/10, section 1,
 * « Fiche résumée ») : l'identite du projet en tete (initiales, nom, client et
 * chef de projet, etat), puis une tuile par information, la plus parlante en
 * grand. RIEN N'EST RELU DANS LA DECLARATION : les libelles, les choix et leurs
 * couleurs, les noms du client et du chef de projet sont ceux que la Fiche a
 * deja resolus pour cette fiche (`lecture`, `ProprietesDeVueCanvas`,
 * `@taki/app-runtime`). Un champ que la Fiche ne montre pas (masque par le jeu,
 * retire de la porte) n'a pas de tuile ; une option archivee garde son mot sur
 * la fiche qui la porte encore. Seuls les montants et les dates passent par les
 * habillages du socle (`euros`, le jour), parce que la tuile les met en grand.
 * Les cartes liees (les taches, les pieces jointes) restent a la Fiche, qui
 * reste la vue par defaut.
 *
 * L'AVANCEMENT EST DESSINE PAR UN MOTEUR CUSTOM (`anneau-d-avancement.ts`,
 * JavaScript vanilla), monte par `useMoteurVanilla` : la preuve qu'une vue de
 * fiche accueille les moteurs des portes Canvas. Une autre fiche remonte le
 * moteur (`cle`), une nouvelle valeur sur la meme fiche glisse en place
 * (`poser`).
 *
 * EN LECTURE : on modifie dans la Fiche. La vue ne dessine que la zone 5.
 */

const JOUR = new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
/**
 * LE JOUR D'UNE DATE DU PROJET, au format de la Fiche (« 24/10/2026 ») : un
 * projet range `startAt` et `dueAt` en INSTANTS (`project.ts`), une date pure
 * passe par l'habillage du socle. Vide : `''`.
 */
function jour(valeur: Cell | undefined): string {
  if (valeur instanceof Date) return Number.isNaN(valeur.getTime()) ? '' : JOUR.format(valeur);
  return datePure(typeof valeur === 'string' ? valeur : null);
}

const nombre = (valeur: Cell | undefined): number | null => (typeof valeur === 'number' && Number.isFinite(valeur) ? valeur : null);

/** Une tuile : son libelle, son icone, sa valeur ; pointillee quand elle est vide. */
function Tuile({
  libelle,
  icone,
  vide,
  large,
  children,
}: {
  libelle: string;
  icone: ReactNode;
  vide: boolean;
  large?: boolean;
  children: ReactNode;
}): JSX.Element {
  return (
    <Card
      className={`flex flex-col gap-3 p-4 shadow-none ${vide ? 'border-dashed' : ''} ${large === true ? 'pair:col-span-3' : ''}`}
    >
      <span className="flex items-center gap-2 text-[0.8125rem] text-ink-500">
        {icone}
        {libelle}
      </span>
      {children}
    </Card>
  );
}

/** Ce qu'une tuile vide dit. */
function Aucun({ mot }: { mot: string }): JSX.Element {
  return <span className="text-[0.875rem] text-ink-400">{mot}</span>;
}

const ICONE = 15;

export function VueResume({ fiche, lecture }: ProprietesDeVueCanvas): JSX.Element | null {
  const champ = (nom: string) => lecture?.champ(nom);

  /* L'AVANCEMENT, par le moteur vanilla. Le montage lit la derniere valeur
     connue (une ref : le moteur n'est pas remonte a chaque rendu). */
  const champAvancement = champ('avancement');
  const avancement = nombre(champAvancement?.valeur);
  const libelleAvancement = champAvancement?.libelle ?? '';
  const derniere = useRef({ avancement, libelleAvancement });
  derniere.current = { avancement, libelleAvancement };
  const anneau = useMoteurVanilla<AnneauDAvancement>({
    monter: (hote) => monterAnneau(hote, derniere.current.avancement, derniere.current.libelleAvancement),
    demonter: (moteur) => moteur.detruire(),
    cle: String(fiche?.['id'] ?? ''),
  });
  const { moteur } = anneau;
  useEffect(() => {
    moteur?.poser(avancement, libelleAvancement);
  }, [moteur, avancement, libelleAvancement]);

  if (fiche === null || lecture === null) return null;

  const client = champ('contactId');
  const chef = champ('chefDeProjet');
  const sante = champ('statutSuivi');
  const debut = champ('startAt');
  const echeance = champ('dueAt');
  const budget = champ('budgetAmount');
  const heures = champ('budgetHours');
  const email = champ('emailClient');
  const etiquettes = champ('etiquettes');
  const facturable = champ('facturable');
  const description = champ('description');
  const etat = lecture.statut;
  const jourDebut = jour(debut?.valeur);
  const jourEcheance = jour(echeance?.valeur);
  const montant = nombre(budget?.valeur);
  const nbHeures = nombre(heures?.valeur);
  const nomDuChef = chef?.texte ?? null;
  const sousTitre = [client?.texte ?? null, chef === undefined || nomDuChef === null ? null : `${chef.libelle} : ${nomDuChef}`]
    .filter((mot) => mot !== null)
    .join(' · ');

  return (
    <div className="mx-auto w-full max-w-[64rem] px-5 pb-12 pt-6" data-vue="projets.resume">
      <header className="flex items-center gap-4">
        <Avatar initials={initialsOf(lecture.titre)} size={48} />
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-[1.375rem] font-semibold leading-tight text-ink-900">{lecture.titre}</h2>
          {sousTitre === '' ? null : <p className="truncate text-[0.875rem] text-ink-500">{sousTitre}</p>}
        </div>
        {etat === null ? null : <StatusBadge label={etat.label} tone={etat.tone} />}
      </header>

      <div className="mt-6 grid grid-cols-1 gap-4 pair:grid-cols-3">
        {champAvancement === undefined ? null : (
          <Tuile libelle={champAvancement.libelle} icone={<IconPieChart size={ICONE} />} vide={avancement === null}>
            <div className="flex items-center gap-4">
              <div ref={anneau.ref} className="h-20 w-20 shrink-0" />
              {sante === undefined ? null : (
                <div className="flex min-w-0 flex-col gap-2">
                  <span className="text-[0.8125rem] text-ink-500">{sante.libelle}</span>
                  {sante.choisies[0] === undefined ? (
                    <Aucun mot="Non renseignée" />
                  ) : (
                    <StatusBadge label={sante.choisies[0].label} tone={sante.choisies[0].tone} />
                  )}
                </div>
              )}
            </div>
            {anneau.erreur === null ? null : <Aucun mot={anneau.erreur} />}
          </Tuile>
        )}

        {echeance === undefined ? null : (
          <Tuile libelle={echeance.libelle} icone={<IconCalendarMark size={ICONE} />} vide={jourEcheance === '' && jourDebut === ''}>
            {jourEcheance === '' ? <Aucun mot="Pas d'échéance" /> : <span className="text-[1.125rem] font-semibold text-ink-900">{jourEcheance}</span>}
            {debut === undefined || jourDebut === '' ? null : (
              <span className="text-[0.8125rem] text-ink-500">{`${debut.libelle} : ${jourDebut}`}</span>
            )}
          </Tuile>
        )}

        {budget === undefined ? null : (
          <Tuile libelle={budget.libelle} icone={<IconBusinessIdeaMoney size={ICONE} />} vide={montant === null && nbHeures === null}>
            {montant === null ? <Aucun mot="Pas de budget" /> : <span className="text-[1.125rem] font-semibold text-ink-900">{euros(montant)}</span>}
            {heures === undefined || nbHeures === null ? null : (
              <span className="text-[0.8125rem] text-ink-500">{`${heures.libelle} : ${nbHeures.toLocaleString('fr-FR')}`}</span>
            )}
          </Tuile>
        )}

        {client === undefined ? null : (
          <Tuile libelle={client.libelle} icone={<IconUserSingleNeutralMale size={ICONE} />} vide={client.rendu === null && email?.texte == null}>
            {client.rendu === null ? <Aucun mot="Pas de client" /> : <span className="text-[1rem] font-semibold text-ink-900">{client.rendu}</span>}
            {email?.texte == null ? null : <span className="truncate text-[0.8125rem] text-ink-500">{email.texte}</span>}
          </Tuile>
        )}

        {chef === undefined ? null : (
          <Tuile libelle={chef.libelle} icone={<IconUserSingleNeutralMale size={ICONE} />} vide={nomDuChef === null}>
            {nomDuChef === null ? (
              <Aucun mot="Non renseigné" />
            ) : (
              <span className="flex items-center gap-3">
                <Avatar initials={initialsOf(nomDuChef)} size={32} />
                <span className="truncate text-[1rem] font-semibold text-ink-900">{nomDuChef}</span>
              </span>
            )}
          </Tuile>
        )}

        {etiquettes === undefined ? null : (
          <Tuile libelle={etiquettes.libelle} icone={<IconSignHashtag size={ICONE} />} vide={etiquettes.choisies.length === 0}>
            {etiquettes.choisies.length === 0 ? (
              <Aucun mot="Aucune étiquette" />
            ) : (
              <span className="flex flex-wrap gap-2">
                {etiquettes.choisies.map((choix) => (
                  <StatusBadge key={choix.value} label={choix.label} tone={choix.tone} />
                ))}
              </span>
            )}
            {facturable === undefined ? null : (
              <span className="text-[0.8125rem] text-ink-500">
                {`${facturable.libelle} : ${facturable.valeur === true ? 'Oui' : facturable.valeur === false ? 'Non' : 'Non renseigné'}`}
              </span>
            )}
          </Tuile>
        )}

        {description === undefined ? null : (
          <Tuile libelle={description.libelle} icone={<IconTextBar size={ICONE} />} vide={description.texte === null} large>
            {description.texte === null ? (
              <Aucun mot="Pas encore de description." />
            ) : (
              <p className="whitespace-pre-line text-[0.875rem] text-ink-900">{description.texte}</p>
            )}
          </Tuile>
        )}
      </div>
    </div>
  );
}
